import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Info, SquarePen } from "lucide-react";
import { formatEsiNumber } from "./TaxHelpers";

/* ---------------- Component ---------------- */

/**
 * Renders the ESI configuration form and view.
 * @param {object} props
 * @param {object} props.esiForm - The current ESI form state.
 * @param {function} props.setEsiForm - Function to update ESI form state.
 * @param {object | null} props.esiView - The saved ESI configuration for view mode.
 * @param {boolean} props.isViewMode - Whether to show the view or edit screen.
 * @param {function} props.setViewMode - Function to toggle view/edit mode.
 * @param {function} props.handleSave - Function to save the configuration.
 * @param {function} props.handleCancel - Function to cancel the edit.
 * @param {function} props.handleDisable - Function to disable the configuration.
 * @param {string | null} props.savingSection - The section currently being saved.
 * @returns {JSX.Element}
 */
const EsiConfiguration = ({
  esiForm,
  setEsiForm,
  esiView,
  isViewMode,
  setViewMode,
  handleSave,
  handleCancel,
  handleDisable,
  savingSection,
  canEditTaxConfig,
}) => {
  const handleEsiNumberChange = useCallback(
    (e) => {
      const formatted = formatEsiNumber(e.target.value);
      setEsiForm((prev) => ({
        ...prev,
        esi_number: formatted,
      }));
      // Add validation logic here if needed, similar to EPF
    },
    [setEsiForm],
  );

  const handleInputChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setEsiForm((prev) => ({
        ...prev,
        [field]: parseFloat(value),
      }));
    },
    [setEsiForm],
  );

  const handleCheckboxChange = useCallback(
    (field) => (checked) => {
      setEsiForm((prev) => ({
        ...prev,
        [field]: !!checked,
      }));
    },
    [setEsiForm],
  );

  const renderESIContent = () => (
    <div className="grid grid-cols-3 gap-6">
      {/* Left: Form Fields */}
      <div className="col-span-2 space-y-6">
        {/* ESI Number */}
        <div>
          <Label htmlFor="esi_number">ESI Number</Label>
          <Input
            disabled={!canEditTaxConfig}
            id="esi_number"
            placeholder="00-00-000000-000-0000"
            className="mt-2"
            value={esiForm.esi_number}
            onChange={handleEsiNumberChange}
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: 00-00-000000-000-0000
          </p>
        </div>

        {/* Deduction Cycle */}
        <div>
          <Label htmlFor="esi_deduction_cycle">
            Deduction Cycle
            <Info className="inline w-4 h-4 ml-1 text-gray-400" />
          </Label>
          <Select
            value={esiForm.deduction_cycle}
            disabled={true} // Locked to monthly
            onValueChange={(value) =>
              setEsiForm((prev) => ({
                ...prev,
                deduction_cycle: value,
              }))
            }
          >
            <SelectTrigger className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Contribution Fields - Side by Side */}
        <div className="grid grid-cols-2 gap-6">
          {/* Employee Contribution */}
          <div className="space-y-1">
            <Label>Employee Contribution</Label>
            <div className="relative">
              <Input
                disabled={!canEditTaxConfig}
                type="number"
                onWheel={(e) => e.target.blur()}
                value={esiForm.employeeContribution ?? 0.75} // Default from helper
                onChange={handleInputChange("employeeContribution")}
                className="pr-10"
              />
              <span className="absolute right-3 top-2.5 text-sm text-gray-500">
                %
              </span>
            </div>
            <p className="text-xs text-gray-500">of Gross Pay</p>
          </div>

          {/* Employer Contribution */}
          <div className="space-y-1">
            <Label>Employer's Contribution</Label>
            <div className="relative">
              <Input
                disabled={!canEditTaxConfig}
                type="number"
                onWheel={(e) => e.target.blur()}
                value={esiForm.employerContribution ?? 3.25} // Default from helper
                onChange={handleInputChange("employerContribution")}
                className="pr-10"
              />
              <span className="absolute right-3 top-2.5 text-sm text-gray-500">
                %
              </span>
            </div>
            <p className="text-xs text-gray-500">of Gross Pay</p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Note:</strong> ESI deductions apply only when salary ≤
            ₹21,000. If salary increases above this limit, ESI continues until
            the current contribution period ends (April–September or
            October–March).
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 pt-6">
          <Button
            onClick={() => {
              if (!canEditTaxConfig) return;
              handleSave("esi")
            }}
            disabled={savingSection === "esi"}
          >
            {savingSection === "esi" ? "Saving..." : "Save"}
          </Button>

          <Button onClick={handleCancel} variant="outline">
            Cancel
          </Button>
        </div>
      </div>

      {/* Right Side: Empty (or later add a Sample Calculation like EPF) */}
      <div></div>
    </div>
  );

  const renderESIView = () => (
    <div className="col-span-2 space-y-6 text-gray-700 dark:text-gray-300">
      <div className="flex gap-3 items-center">
        <span className="font-extrabold text-lg text-black dark:text-white">
          Employees' State Insurance
        </span>
        <SquarePen
          onClick={() => {
            if (!canEditTaxConfig) return;
            setViewMode((prev) => ({ ...prev, esi: false }));
          }}
          className={`p-1 rounded
          ${
            canEditTaxConfig
              ? "text-gray-600 hover:text-[#3194A0]"
              : "text-gray-300 cursor-not-allowed"
          }
        `}
        />
      </div>

      <div className="space-y-6 text-gray-700 dark:text-gray-300">
        <div>
          <p className="font-semibold">ESI Number</p>
          <p>{esiView?.esi_number || "--"}</p>
        </div>

        <div>
          <p className="font-semibold">Deduction Cycle</p>
          <p>{esiView?.deduction_cycle || "--"}</p>
        </div>

        <div>
          <p className="font-semibold">Employee Contribution</p>
          <p>{esiView?.employeeContribution ?? 0.75}% of Gross Pay</p>
        </div>

        <div>
          <p className="font-semibold">Employer Contribution</p>
          <p>{esiView?.employerContribution ?? 3.25}% of Gross Pay</p>
        </div>

        <div>
          <p className="font-semibold">Other Details</p>
          <p>
            {esiView?.include_employer_contribution
              ? "Employer's contribution is included in employee’s salary structure."
              : "Employer's contribution is NOT included in salary structure."}
          </p>
        </div>

        <Button
          variant="destructive"
          onClick={() => {
            if (!canEditTaxConfig) return;
            handleDisable("esi");
          }}
        >
          Disable ESI
        </Button>
      </div>
    </div>
  );

  return isViewMode ? renderESIView() : renderESIContent();
};

export default EsiConfiguration;
